/**
 * ThemeUploadArea Component
 * Drag-and-drop upload area for theme folders
 */

import React, { DragEvent, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Upload, Folder, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import api from '../../services/api';

interface FileWithPath {
  file: File;
  relativePath: string;
}

interface LayoutInfo {
  layoutId: string;
  hasSlideJson: boolean;
  hasLayoutCapabilities: boolean;
  files: string[];
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  themeName: string;
  layouts: LayoutInfo[];
  hasTemplateCapabilities: boolean;
}

type UploadState = 'idle' | 'validating' | 'uploading' | 'complete' | 'error';

interface ThemeUploadAreaProps {
  onUploadComplete?: () => void;
}

export const ThemeUploadArea: React.FC<ThemeUploadAreaProps> = ({
  onUploadComplete,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  // Helper function to recursively read all files from a directory entry
  const readDirectoryRecursively = async (
    directoryEntry: FileSystemDirectoryEntry,
    basePath: string = ''
  ): Promise<FileWithPath[]> => {
    const files: FileWithPath[] = [];

    return new Promise((resolve) => {
      const directoryReader = directoryEntry.createReader();

      const readEntries = () => {
        directoryReader.readEntries(async (entries: FileSystemEntry[]) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }

          for (const entry of entries) {
            const entryPath = basePath ? `${basePath}/${entry.name}` : entry.name;

            if (entry.isFile) {
              const file = await new Promise<File>((resolveFile) => {
                (entry as FileSystemFileEntry).file(resolveFile);
              });
              files.push({ file, relativePath: entryPath });
            } else if (entry.isDirectory) {
              const subFiles = await readDirectoryRecursively(
                entry as FileSystemDirectoryEntry,
                entryPath
              );
              files.push(...subFiles);
            }
          }

          // Continue reading (directories may have more entries than fit in one read)
          readEntries();
        });
      };

      readEntries();
    });
  };

  // Validate theme structure client-side
  const validateThemeStructure = (files: FileWithPath[]): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const layoutMap = new Map<string, LayoutInfo>();
    let hasTemplateCapabilities = false;

    // Extract theme name from first path
    let themeName = 'unknown-theme';
    if (files.length > 0) {
      const firstPath = files[0].relativePath;
      const parts = firstPath.split('/');
      if (parts.length > 0) {
        themeName = parts[0];
      }
    }

    // Group files by layout and check for theme-level files
    for (const { relativePath } of files) {
      const pathParts = relativePath.split('/');
      const fileName = pathParts[pathParts.length - 1];

      // Check for theme-level template-capabilities.json
      const hasLayoutInPath = pathParts.some(part => /^layout_/.test(part));
      if (!hasLayoutInPath && fileName === 'template-capabilities.json') {
        hasTemplateCapabilities = true;
      }

      // Find layout_XX in the path (support both layout_01 and layout_name patterns)
      let layoutId: string | null = null;
      let layoutRelativePath: string = '';

      for (let i = 0; i < pathParts.length; i++) {
        if (/^layout_/.test(pathParts[i])) {
          layoutId = pathParts[i];
          layoutRelativePath = pathParts.slice(i + 1).join('/');
          break;
        }
      }

      if (!layoutId) continue;

      if (!layoutMap.has(layoutId)) {
        layoutMap.set(layoutId, {
          layoutId,
          hasSlideJson: false,
          hasLayoutCapabilities: false,
          files: [],
        });
      }

      const layoutInfo = layoutMap.get(layoutId)!;
      layoutInfo.files.push(relativePath);

      if (layoutRelativePath === 'slide.json') {
        layoutInfo.hasSlideJson = true;
      } else if (layoutRelativePath === 'layout-capabilities.json') {
        layoutInfo.hasLayoutCapabilities = true;
      }
    }

    // Validate layouts
    const validLayouts: LayoutInfo[] = [];

    for (const [layoutId, layoutInfo] of layoutMap) {
      if (!layoutInfo.hasSlideJson) {
        errors.push(`${layoutId}: Missing required slide.json`);
        continue;
      }
      if (!layoutInfo.hasLayoutCapabilities) {
        warnings.push(`${layoutId}: No layout-capabilities.json found`);
      }
      validLayouts.push(layoutInfo);
    }

    if (validLayouts.length === 0) {
      errors.push('No valid layouts found. Each layout folder must contain slide.json');
    }

    if (!hasTemplateCapabilities) {
      warnings.push('No template-capabilities.json found at theme level');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      themeName,
      layouts: validLayouts,
      hasTemplateCapabilities,
    };
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setUploadState('validating');
    setError(null);

    try {
      const allFiles: FileWithPath[] = [];

      // Check if we have DataTransferItemList (supports directories)
      if (e.dataTransfer.items) {
        const items = Array.from(e.dataTransfer.items);

        for (const item of items) {
          const entry = item.webkitGetAsEntry();

          if (entry) {
            if (entry.isFile) {
              const file = await new Promise<File>((resolve) => {
                (entry as FileSystemFileEntry).file(resolve);
              });
              allFiles.push({ file, relativePath: entry.name });
            } else if (entry.isDirectory) {
              const dirFiles = await readDirectoryRecursively(
                entry as FileSystemDirectoryEntry,
                entry.name
              );
              allFiles.push(...dirFiles);
            }
          }
        }
      }

      if (allFiles.length === 0) {
        setError('No files found. Please drop a theme folder.');
        setUploadState('error');
        return;
      }

      // Validate structure
      const validationResult = validateThemeStructure(allFiles);
      setValidation(validationResult);
      setFiles(allFiles);

      if (!validationResult.valid) {
        setUploadState('error');
        setError(validationResult.errors.join('\n'));
        return;
      }

      setUploadState('idle');
    } catch (error) {
      console.error('Error processing dropped files:', error);
      setError('Failed to process files. Please try again.');
      setUploadState('error');
    }
  };

  const handleDirectoryInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploadState('validating');
    setError(null);

    try {
      // Convert FileList to FileWithPath array
      const allFiles: FileWithPath[] = Array.from(selectedFiles).map(file => ({
        file,
        relativePath: (file as any).webkitRelativePath || file.name,
      }));

      // Validate structure
      const validationResult = validateThemeStructure(allFiles);
      setValidation(validationResult);
      setFiles(allFiles);

      if (!validationResult.valid) {
        setUploadState('error');
        setError(validationResult.errors.join('\n'));
        return;
      }

      setUploadState('idle');
    } catch (error) {
      console.error('Error processing files:', error);
      setError('Failed to process files. Please try again.');
      setUploadState('error');
    }

    // Reset input
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!validation || !validation.valid || files.length === 0) return;

    setUploadState('uploading');
    setUploadProgress(0);

    try {
      const formData = new FormData();

      // Add files
      files.forEach(({ file }) => {
        formData.append('files', file);
      });

      // Add file paths
      formData.append('filePaths', JSON.stringify(files.map(f => f.relativePath)));

      // Add theme name
      formData.append('themeName', validation.themeName);

      const response = await api.post('/admin/themes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setUploadResult(response.data);
        setUploadState('complete');
        onUploadComplete?.();
      } else {
        setError(response.data.error || 'Upload failed');
        setUploadState('error');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || error.message || 'Upload failed');
      setUploadState('error');
    }
  };

  const handleReset = () => {
    setFiles([]);
    setValidation(null);
    setUploadState('idle');
    setUploadProgress(0);
    setError(null);
    setUploadResult(null);
  };

  // Complete state
  if (uploadState === 'complete' && uploadResult) {
    return (
      <div className="border rounded-lg bg-green-50 p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-green-900">Upload Complete</h3>
            <p className="text-sm text-green-700 mt-1">
              Theme "{uploadResult.themeName}" uploaded successfully.
            </p>
            <div className="mt-3 text-sm text-green-700">
              <p>• {uploadResult.layoutsUploaded} layouts uploaded</p>
              <p>• {uploadResult.templatesCreated} templates created</p>
              {uploadResult.screenshotJobsQueued > 0 && (
                <p>• {uploadResult.screenshotJobsQueued} preview screenshots queued</p>
              )}
            </div>
            {uploadResult.validationWarnings?.length > 0 && (
              <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-800">
                <p className="font-medium">Warnings:</p>
                {uploadResult.validationWarnings.map((w: string, i: number) => (
                  <p key={i}>• {w}</p>
                ))}
              </div>
            )}
            <Button onClick={handleReset} variant="outline" className="mt-4">
              Upload Another Theme
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (uploadState === 'error') {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-red-900">Upload Failed</h3>
            <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</p>
            {validation?.errors && validation.errors.length > 0 && (
              <div className="mt-3 text-sm text-red-700">
                {validation.errors.map((err, i) => (
                  <p key={i}>• {err}</p>
                ))}
              </div>
            )}
            <Button onClick={handleReset} variant="outline" className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Uploading state
  if (uploadState === 'uploading') {
    return (
      <div className="border rounded-lg bg-card p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="font-medium">Uploading theme...</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-sm text-muted-foreground">
            Uploading {files.length} files ({uploadProgress}%)
          </p>
        </div>
      </div>
    );
  }

  // Validating state
  if (uploadState === 'validating') {
    return (
      <div className="border rounded-lg bg-card p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="font-medium">Validating theme structure...</span>
        </div>
      </div>
    );
  }

  // Files staged - ready for upload
  if (validation && validation.valid && files.length > 0) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg bg-card p-6">
          <div className="flex items-start gap-3">
            <Folder className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-medium">Theme Ready to Upload</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Theme: <span className="font-medium text-foreground">{validation.themeName}</span>
              </p>
              <div className="mt-3 text-sm">
                <p className="text-muted-foreground">
                  {validation.layouts.length} layouts found • {files.length} files total
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {validation.layouts.map((layout) => (
                    <span
                      key={layout.layoutId}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs"
                    >
                      {layout.layoutId}
                    </span>
                  ))}
                </div>
              </div>
              {validation.warnings.length > 0 && (
                <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-800">
                  <p className="font-medium">Warnings:</p>
                  {validation.warnings.map((w, i) => (
                    <p key={i}>• {w}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleUpload} className="flex-1">
            <Upload className="h-4 w-4 mr-2" />
            Upload Theme
          </Button>
          <Button onClick={handleReset} variant="outline">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Idle state - drop zone
  return (
    <div className="space-y-3">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
          isDragOver ? "border-primary bg-primary/5" : "border-border bg-muted/20"
        )}
      >
        <Upload
          className={cn(
            "h-10 w-10 mx-auto mb-4",
            isDragOver ? "text-primary" : "text-muted-foreground"
          )}
        />
        <p className="text-base font-medium mb-1">
          {isDragOver ? "Drop theme folder here" : "Drag and drop theme folder here"}
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Theme folder should contain layout_01, layout_02, etc. with slide.json files
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => directoryInputRef.current?.click()}
        >
          <Folder className="h-4 w-4 mr-2" />
          Browse Folder
        </Button>
      </div>

      {/* Hidden Directory Input */}
      <input
        ref={directoryInputRef}
        type="file"
        /* @ts-ignore - webkitdirectory is not in standard types */
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={handleDirectoryInputChange}
      />

      {/* Instructions */}
      <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-4">
        <p className="font-medium mb-2">Theme folder structure:</p>
        <pre className="text-xs bg-muted rounded p-2 overflow-x-auto">
{`my-theme/
├── template-capabilities.json  (recommended - theme capabilities)
├── layout_01/
│   ├── slide.json              (required)
│   ├── layout-capabilities.json (recommended - layout capabilities)
│   ├── preview.png             (optional - will be generated)
│   └── source/
│       ├── metadata.json
│       └── animations.json
├── layout_02/
│   └── ...`}
        </pre>
      </div>
    </div>
  );
};
